// @flow
import { createContext, useEffect, useState, useCallback } from 'react';
import _debounce from 'lodash/debounce';

import useAxios from '../hooks/useAxios';
import { apiCanonicalUrl } from '../app_routes/general';
import { handleError } from '../utils/errors';

import type { Node } from 'react';

export const CanonicalUrlStateContext = createContext();

export const CanonicalUrlProvider = ({
  children,
  debounceRate = 0,
  forcedValue = null
}: {
  children: null | Node,
  debounceRate?: number,
  forcedValue?: string
}) => {
  const [url, setUrl] = useState(forcedValue);
  const axios = useAxios();

  const handlePopState = useCallback(
    _debounce(() => {
      if (typeof window === 'undefined') return;

      axios
        .post(apiCanonicalUrl, { url: window.location.href })
        .then(res => {
          setUrl(res.data);

          const canonicalEl = document.querySelector("link[rel='canonical']");

          if (canonicalEl) {
            canonicalEl.setAttribute('href', res.data);
          }
        })
        .catch(handleError);
    }, debounceRate),
    [axios]
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    // we don't want to update the value if we are forcing a value
    if (forcedValue) return;

    const canonicalEl = document.querySelector("link[rel='canonical']");

    if (canonicalEl) {
      setUrl(canonicalEl.getAttribute('href'));
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('popstate', handlePopState);
    }

    return () => {
      if (typeof window === 'undefined') return;

      window.removeEventListener('popstate', handlePopState);
    };
  }, [handlePopState, forcedValue]);

  return (
    <CanonicalUrlStateContext.Provider value={{ url }}>
      {children}
    </CanonicalUrlStateContext.Provider>
  );
};
